import {Collection} from './Collection';
import {Package} from './Package';
import {FileManager} from '../../shared/Models/FileManager';
import { PartProduct } from './Part';

/**
 * A preset width × length shortcut on a rug.
 *
 * It may also carry its own fixed price, which replaces the material leg of the per-m²
 * formula when the ordered dimensions match it. That is deliberate technical debt from the
 * client change request of 2026-08-28 (US-597 / US-596): fixed-size rugs are mass-produced
 * and sell below their calculated price. It supersedes US-508's "no price column, ever",
 * whose reasoning — a second source of truth reintroduces the divergence the formula exists
 * to prevent — was correct and still holds for every preset that does not opt in.
 *
 * Both prices or neither, validated here and on the server. See resolveFixedSizePrice() in
 * helpers/carpet-price.ts, and `OrderService::applyCarpetCost()`, which implements the
 * identical rule — both sides must change together.
 */
export class ProductSize {
  public id?: number;
  public widthCm: number;
  public lengthCm: number;
  public position?: number;
  /** Excl. VAT — same convention as Product.dealerPrice */
  public fixedDealerPrice?: number | null;
  /** Incl. VAT — same convention as Product.customerPrice */
  public fixedCustomerPrice?: number | null;
}

export class Product {
  static PRODUCT_TYPE_PRODUCT = 'product';
  static PRODUCT_TYPE_GROUPED_PRODUCT = 'grouped product';
  static PRODUCT_ACTIVE = 'active';
  static PRODUCT_INACTIVE = 'inactive';
  /**
   * Order export layouts
   */
  static layout_1 = 'layout1';
  static layout_2 = 'layout2';
  static layout_stock = 'layout_stock';
  /**
   * Carpets & Rugs — a product priced per m² is a rug (spec FR-001/FR-002)
   */
  static PRICING_MODE_PER_UNIT = 'per_unit';
  static PRICING_MODE_PER_SQUARE_METER = 'per_square_meter';
  static SIZING_MODE_FIXED = 'fixed';
  static SIZING_MODE_CUSTOM = 'custom';

  static isRug(product: any): boolean {
    return !!product && product.pricingMode === Product.PRICING_MODE_PER_SQUARE_METER;
  }

  /**
   * D26: `errors.range` names both bounds, so an unconfigured one interpolated to null
   * and ngx-translate left the raw `{{min}}` / `{{max}}` token on screen. Name only the
   * bounds that exist, reusing the three messages the server already validates with
   * (`ValidProductSizeDimensionsValidator`), so the browser and the API say the same
   * thing. `Number.isFinite` is the "is this bound configured" test: the API sends null,
   * not undefined, for an unset limit.
   *
   * The doubled `validators.validators.` prefix is not a typo — the domain is the file
   * name (`validators.en.yml`) and the message id is the yml path (`validators.
   * product_size.…`). Do not "tidy" either half.
   */
  static sizeRangeKey(axis: 'width' | 'length', min?: number, max?: number): string {
    const hasMin = Number.isFinite(min);
    const hasMax = Number.isFinite(max);
    let variant = 'below_minimum';
    if (hasMin && hasMax) {
      variant = 'outside_range';
    } else if (hasMax) {
      variant = 'above_maximum';
    }
    return `validators.validators.product_size.${axis}.${variant}`;
  }

  public id: number;
  public number: string;
  public name: string;
  public code: string;
  public description?: string;
  public dealerPrice: number;
  public customerPrice: number;
  public collection: Collection;
  public status: string;
  public hasProduct = false;
  public packages: Package[] = [];
  public translations: any;
  public optionProducts?: any;
  public manageStock?: boolean;
  public EANCode?: string;
  public stock?: string;
  public ownStock?: string;
  public lowStockTrigger?: string;
  public emailStockTrigger?: string;
  public productProperties?: any;
  public lowStackTrigger?: string;
  public image: FileManager;
  public countryDealerPrice: number;
  public countryCustomerPrice: number;
  public productPropertyStyles: any;
  public parentPosition: number;
  public excludeInPackagingLabel?: boolean;
  public hasFabric?: boolean;
  public amount?: number;
  public product?: any;
  public deliveryCostPerArticle?: number;
  public partProducts?: PartProduct[];
  public pricingMode?: string;
  public sizingMode?: string;
  public minWidthCm?: number;
  public maxWidthCm?: number;
  public minLengthCm?: number;
  public maxLengthCm?: number;
  public productSizes?: ProductSize[];
  constructor() {
    this.packages.push(new Package());
    this.translations = [];
  }
}
